import { graphqlClient } from '@/lib/graphql-client';
import {
  buildGetDepartmentsLookupQuery,
  buildGetDesignationsLookupQuery,
  buildGetEmployeesQuery,
} from '../graphql/queries';
import { DepartmentLookupOption, EmployeeOption, LookupOption } from '../types/lookup.types';

const LOOKUP_PAGE_SIZE = 300;

/** Every option fits in one page, so lookup lists are sorted client-side by `Title` ascending,
 * matching the old `{"Title":1}` sort. */
const sortByTitleAsc = <T extends LookupOption>(items: T[]): T[] =>
  [...items].sort((a, b) => a.Title.localeCompare(b.Title));

export const getDepartmentOptions = async (): Promise<DepartmentLookupOption[]> => {
  const result = await graphqlClient.query<{ Departments: { items: DepartmentLookupOption[] } }>({
    query: buildGetDepartmentsLookupQuery({}, 1, LOOKUP_PAGE_SIZE),
  });
  return sortByTitleAsc(result.Departments.items);
};

export const getDesignationOptions = async (): Promise<LookupOption[]> => {
  const result = await graphqlClient.query<{ Designations: { items: LookupOption[] } }>({
    query: buildGetDesignationsLookupQuery({}, 1, LOOKUP_PAGE_SIZE),
  });
  return sortByTitleAsc(result.Designations.items);
};

const EMPLOYEE_SEARCH_PAGE_SIZE = 20;

/** Employees matching a name fragment (optionally within one unit); an empty keyword returns the first page.
 * Newest employee first, sorted on the server, like the old `employee/basicInfos` `{ CreateDate: -1 }` sort. */
export const searchEmployees = async (
  keyword = '',
  departmentId?: string
): Promise<EmployeeOption[]> => {
  const filter = {
    ...(keyword ? { DisplayName: { $regex: keyword, $options: 'i' } } : {}),
    ...(departmentId ? { DepartmentId: departmentId } : {}),
  };
  const result = await graphqlClient.query<{ Employees: { items: EmployeeOption[] } }>({
    query: buildGetEmployeesQuery(filter, 1, EMPLOYEE_SEARCH_PAGE_SIZE, [
      { field: 'CreatedDate', direction: 'DESC' },
    ]),
  });
  return result.Employees.items;
};

/** Resolves a batch of IAM user ids (the id review records store) to employees. */
export const getEmployeesByUserIds = async (userIds: string[]): Promise<EmployeeOption[]> => {
  if (userIds.length === 0) return [];
  const result = await graphqlClient.query<{ Employees: { items: EmployeeOption[] } }>({
    query: buildGetEmployeesQuery({ UserId: { $in: userIds } }, 1, userIds.length),
  });
  return result.Employees.items;
};
